import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiGet, formatNumber, formatPct } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StockSearchDropdown } from "@/components/common/StockSearchDropdown";
import {
  GitCompare,
  X,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Sparkles,
  ShieldAlert,
  Activity,
} from "lucide-react";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const MAX_SYMBOLS = 3;

// Each row in the comparison table — a metric the user wants to compare.
// `value(r)` extracts the comparable number; `format(v, r)` produces the
// display string; `better(a, b)` returns +1 if a is better than b, -1 if
// worse, 0 if equal (used to highlight the best stock in each row).
const ROW_GROUPS = [
  {
    title: "Score",
    icon: Sparkles,
    rows: [
      {
        label: "Overall score",
        value: (r) => r?.score?.final_score ?? null,
        format: (v) => (v == null ? "—" : `${v}/100`),
        better: (a, b) => (a ?? -1) - (b ?? -1),
      },
      {
        label: "Signal",
        value: (r) => r?.score?.signal ?? null,
        format: (v) => v || "—",
        // BUY > WATCH > HOLD > NO_DATA > null
        better: (a, b) => {
          const order = { BUY: 4, WATCH: 3, HOLD: 2, NO_DATA: 1 };
          return (order[a] || 0) - (order[b] || 0);
        },
        renderBadge: (v) => {
          const variants = {
            BUY: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
            WATCH: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
            HOLD: "bg-muted text-muted-foreground border",
            NO_DATA: "bg-muted/50 text-muted-foreground border",
          };
          return (
            <Badge variant="outline" className={cn("text-[11px]", variants[v] || variants.HOLD)}>
              {v || "—"}
            </Badge>
          );
        },
      },
      { label: "Layer 1 — Current Value", value: (r) => r?.score?.layer1_score ?? null, format: (v) => (v == null ? "—" : `${v}/25`) },
      { label: "Layer 2 — Historical Quality", value: (r) => r?.score?.layer2_score ?? null, format: (v) => (v == null ? "—" : `${v}/25`) },
      { label: "Layer 3 — Financial Health", value: (r) => r?.score?.layer3_score ?? null, format: (v) => (v == null ? "—" : `${v}/15`) },
      { label: "Layer 4 — Intrinsic Valuation", value: (r) => r?.score?.layer4_score ?? null, format: (v) => (v == null ? "—" : `${v}/20`) },
      { label: "Layer 5 — Forward Intelligence", value: (r) => r?.score?.layer5_score ?? null, format: (v) => (v == null ? "—" : `${v}/15`) },
      {
        label: "Technical bonus",
        value: (r) => r?.score?.technical_bonus ?? null,
        format: (v) => (v == null ? "—" : (v > 0 ? `+${v}` : `${v}`)),
      },
    ],
  },
  {
    title: "Valuation",
    icon: Activity,
    rows: [
      {
        label: "Price (LKR)",
        value: (r) => r?.price?.close ?? null,
        format: (v) => (v == null ? "—" : `Rs ${formatNumber(v, 2)}`),
        better: () => 0, // not better/worse
      },
      {
        label: "P/E ratio",
        value: (r) => r?.fundamentals?.pe_ratio ?? null,
        format: (v) => (v == null ? "—" : formatNumber(v, 2)),
        better: (a, b) => {
          // lower PE wins (skip if missing or non-positive)
          const va = a == null || a <= 0 ? Infinity : a;
          const vb = b == null || b <= 0 ? Infinity : b;
          return vb - va;
        },
      },
      {
        label: "P/B ratio",
        value: (r) => r?.fundamentals?.pb_ratio ?? null,
        format: (v) => (v == null ? "—" : formatNumber(v, 2)),
        better: (a, b) => {
          const va = a == null || a <= 0 ? Infinity : a;
          const vb = b == null || b <= 0 ? Infinity : b;
          return vb - va;
        },
      },
      {
        label: "Dividend yield",
        value: (r) => r?.fundamentals?.dividend_yield ?? null,
        format: (v) => formatPct(v, 2),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
      {
        label: "Graham Number",
        value: (r) => r?.score?.layer4_graham_number ?? null,
        format: (v) => (v == null ? "—" : `Rs ${formatNumber(v, 2)}`),
      },
      {
        label: "DDM Fair Value",
        value: (r) => r?.score?.layer4_ddm_fair_value ?? null,
        format: (v) => (v == null ? "—" : `Rs ${formatNumber(v, 2)}`),
      },
    ],
  },
  {
    title: "Quality & Health",
    icon: ShieldAlert,
    rows: [
      {
        label: "ROE",
        value: (r) => r?.fundamentals?.roe ?? null,
        format: (v) => formatPct(v, 1),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
      {
        label: "Debt-to-Equity",
        value: (r) => r?.fundamentals?.debt_to_equity ?? null,
        format: (v) => (v == null ? "—" : formatNumber(v, 2)),
        better: (a, b) => {
          // lower D/E wins (treat null as no data)
          const va = a == null ? Infinity : a;
          const vb = b == null ? Infinity : b;
          return vb - va;
        },
      },
      {
        label: "EPS (latest)",
        value: (r) => r?.fundamentals?.eps ?? null,
        format: (v) => (v == null ? "—" : formatNumber(v, 2)),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
      {
        label: "DPS (per share)",
        value: (r) => r?.fundamentals?.dividend_per_share ?? null,
        format: (v) => (v == null ? "—" : formatNumber(v, 2)),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
      {
        label: "Revenue growth",
        value: (r) => r?.fundamentals?.revenue_growth ?? null,
        format: (v) => formatPct(v, 1),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
      {
        label: "EPS history (years)",
        value: (r) => r?.score?.layer2_eps_history_years ?? null,
        format: (v) => (v == null ? "—" : `${v}y`),
      },
      {
        label: "EPS growth basis",
        value: (r) => r?.score?.layer2_eps_growth_basis ?? null,
        format: (v) => (v ? v.replace(/_/g, " ") : "—"),
      },
      {
        label: "EPS CAGR",
        value: (r) => r?.score?.layer2_eps_cagr ?? null,
        format: (v) => formatPct(v, 1),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
    ],
  },
  {
    title: "Technicals",
    icon: Activity,
    rows: [
      {
        label: "TV recommendation",
        value: (r) => r?.technicals?.recommendation ?? null,
        format: (v) => v || "—",
      },
      {
        label: "RSI",
        value: (r) => r?.technicals?.rsi ?? null,
        format: (v, r) => {
          if (v == null) return "—";
          const fallback = r?.technicals?.is_fallback;
          const src = r?.technicals?.source_symbol;
          // Phase 6 (Item 23) — when the RSI was sourced from the
          // voting share class as a fallback, append a small "(via N)"
          // tag so users know this is a proxy. Symbols are otherwise
          // economically near-identical.
          return fallback
            ? `${formatNumber(v, 1)} (via ${src})`
            : formatNumber(v, 1);
        },
      },
      {
        label: "Day change",
        value: (r) => r?.price?.change_pct ?? null,
        format: (v) => formatPct(v, 2),
        better: (a, b) => (a ?? -Infinity) - (b ?? -Infinity),
      },
    ],
  },
];

const FlagsRow = ({ stocks }) => {
  const flagItems = (r) => {
    if (!r?.found) return null;
    const f = r.flags || {};
    const list = [];
    if (f.lumpy_earnings) list.push({ label: "Lumpy EPS", icon: AlertTriangle, tone: "warn" });
    if (f.special_dividend_detected) list.push({ label: "Special dividend", icon: AlertTriangle, tone: "warn" });
    if (f.is_investment_trust) list.push({ label: "Investment trust", icon: ShieldAlert, tone: "info" });
    if (f.low_volume_warning) list.push({ label: "Low volume", icon: AlertTriangle, tone: "warn" });
    return list;
  };
  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-3 text-xs text-muted-foreground sticky left-0 bg-card whitespace-nowrap">
        Flags
      </td>
      {stocks.map((s, i) => {
        const items = flagItems(s) || [];
        return (
          <td key={i} className="px-4 py-3" data-testid={`compare-flags-${i}`}>
            {items.length === 0 ? (
              <span className="text-xs text-muted-foreground">—</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {items.map((it, idx) => {
                  const Icon = it.icon;
                  const cls = it.tone === "warn"
                    ? "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40"
                    : "bg-primary/10 text-primary border-primary/30";
                  return (
                    <Badge
                      key={idx}
                      variant="outline"
                      className={cn("text-[10px] inline-flex items-center gap-1", cls)}
                    >
                      <Icon className="h-2.5 w-2.5" />
                      {it.label}
                    </Badge>
                  );
                })}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
};

const computeBestIndex = (row, stocks) => {
  if (!row.better) return -1;
  const values = stocks.map((s) => (s?.found ? row.value(s) : null));
  let bestIdx = -1;
  let bestVal = null;
  values.forEach((v, i) => {
    if (v == null || (typeof v === "number" && !isFinite(v))) return;
    if (bestIdx === -1 || row.better(v, bestVal) > 0) {
      bestIdx = i;
      bestVal = v;
    }
  });
  // Verify bestVal is strictly better than the others (no tie)
  if (bestIdx !== -1) {
    const tied = values.filter((v, i) => i !== bestIdx && v != null && row.better(v, bestVal) === 0);
    if (tied.length) return -1;
  }
  return bestIdx;
};

const ComparePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const symbols = useMemo(() => {
    const raw = searchParams.get("stocks") || "";
    return raw.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean).slice(0, MAX_SYMBOLS);
  }, [searchParams]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allSecurities, setAllSecurities] = useState([]);

  // Load securities list once for the typeahead dropdown
  useEffect(() => {
    apiGet("/securities")
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.items || [];
        setAllSecurities(list);
      })
      .catch(() => setAllSecurities([]));
  }, []);

  useEffect(() => {
    if (symbols.length === 0) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    apiGet(`/compare?symbols=${symbols.join(",")}`)
      .then((res) => setData(res))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load comparison"))
      .finally(() => setLoading(false));
  }, [symbols.join(",")]);

  const stocks = data?.results || [];

  const updateSymbols = (next) => {
    const sanitized = Array.from(new Set(next.map((s) => s.trim().toUpperCase()).filter(Boolean))).slice(0, MAX_SYMBOLS);
    if (sanitized.length === 0) {
      navigate("/compare", { replace: true });
      return;
    }
    setSearchParams({ stocks: sanitized.join(",") }, { replace: true });
  };

  const handleRemove = (sym) => {
    updateSymbols(symbols.filter((s) => s !== sym));
  };

  const colCount = Math.max(stocks.length, 1);

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-xl font-semibold flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-primary" />
            Compare Stocks
          </h1>
          <p className="text-sm text-muted-foreground">
            Side-by-side scores, valuation, quality & technicals for up to {MAX_SYMBOLS} stocks. Best value per row is highlighted.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>
      </div>

      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base">Stocks selected</CardTitle>
          <CardDescription>
            Enter CSE symbols (e.g. <code className="text-[11px] bg-muted px-1 py-0.5 rounded">COMB.N0000</code>) — up to {MAX_SYMBOLS}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {symbols.map((s, i) => {
              const found = stocks[i]?.found !== false;
              return (
                <Badge
                  key={s}
                  variant="outline"
                  className={cn(
                    "gap-2 pl-3 pr-1.5 py-1.5 text-xs",
                    !found && "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
                  )}
                  data-testid={`compare-chip-${s}`}
                >
                  <span className="font-mono">{s}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(s)}
                    className="h-5 w-5 hover:bg-muted"
                    aria-label={`Remove ${s}`}
                    data-testid={`compare-remove-${s}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            })}

            {symbols.length < MAX_SYMBOLS && (
              <div className="flex items-center gap-2 w-full sm:w-auto" data-testid="compare-add-form">
                <div className="w-72">
                  <StockSearchDropdown
                    securities={allSecurities}
                    excluded={symbols.map((s) => s.toUpperCase())}
                    placeholder="Search ticker or company…"
                    testId="compare-search"
                    size="sm"
                    onChange={(sym) => {
                      if (!sym) return;
                      if (symbols.length >= MAX_SYMBOLS) return;
                      updateSymbols([...symbols, sym]);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {symbols.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Add symbols above to start comparing. You can also click "Compare" on any Stock Detail page or select multiple stocks from the Screener.
            </p>
          )}
        </CardContent>
      </Card>

      {error && (
        <Card className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5">
          <CardContent className="py-4 text-sm text-[hsl(var(--destructive))]">
            {error}
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="border bg-card">
          <CardContent className="py-12 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {!loading && stocks.length > 0 && (
        <Card className="border bg-card overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground sticky left-0 bg-muted/30 z-10 min-w-[160px]"
                    >
                      Metric
                    </th>
                    {stocks.map((s, i) => (
                      <th key={i} className="px-4 py-3 text-left min-w-[200px]" data-testid={`compare-header-${i}`}>
                        {s.found ? (
                          <div className="space-y-0.5">
                            <CompanyLink
                              code={s.company_code}
                              name={s.company_name}
                              className="text-sm font-display font-semibold"
                            />
                            <div className="flex items-center gap-1.5 text-xs">
                              <SymbolLink code={s.company_code} symbol={s.cse_symbol} />
                              {s.share_type === "non_voting" && (
                                <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400">
                                  Non-voting
                                </Badge>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground">{s.sector || "—"}</div>
                          </div>
                        ) : (
                          <div className="text-xs text-[hsl(var(--destructive))] font-mono">
                            {s.cse_symbol}
                            <div className="text-[10px] uppercase tracking-wide">not found</div>
                          </div>
                        )}
                      </th>
                    ))}
                    {/* Empty placeholders to keep columns aligned when fewer than MAX */}
                    {Array.from({ length: Math.max(0, colCount - stocks.length) }).map((_, i) => (
                      <th key={`pad-${i}`} className="px-4 py-3" />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROW_GROUPS.map((group) => {
                    const Icon = group.icon;
                    return (
                      <React.Fragment key={group.title}>
                        <tr className="bg-muted/10 border-b">
                          <td
                            colSpan={1 + colCount}
                            className="px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground sticky left-0 bg-muted/10"
                          >
                            <span className="inline-flex items-center gap-2">
                              <Icon className="h-3 w-3" />
                              {group.title}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map((row, rIdx) => {
                          const bestIdx = computeBestIndex(row, stocks);
                          return (
                            <tr key={rIdx} className="border-b last:border-0 hover:bg-muted/20">
                              <td className="px-4 py-3 text-xs text-muted-foreground sticky left-0 bg-card whitespace-nowrap">
                                {row.label}
                              </td>
                              {stocks.map((s, i) => {
                                const v = s?.found ? row.value(s) : null;
                                const display = row.renderBadge ? row.renderBadge(v) : row.format(v, s);
                                const isBest = bestIdx === i;
                                return (
                                  <td
                                    key={i}
                                    className={cn(
                                      "px-4 py-3 tabular text-sm",
                                      isBest && "bg-[hsl(var(--success))]/10 font-semibold text-[hsl(var(--success))]",
                                    )}
                                    data-testid={`compare-cell-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`}
                                  >
                                    {display}
                                  </td>
                                );
                              })}
                              {Array.from({ length: Math.max(0, colCount - stocks.length) }).map((_, i) => (
                                <td key={`pad-${i}`} className="px-4 py-3" />
                              ))}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {/* Flags row (special — multi-badge cells) */}
                  <tr className="bg-muted/10 border-b">
                    <td
                      colSpan={1 + colCount}
                      className="px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground sticky left-0 bg-muted/10"
                    >
                      <span className="inline-flex items-center gap-2">
                        <AlertTriangle className="h-3 w-3" />
                        Flags
                      </span>
                    </td>
                  </tr>
                  <FlagsRow stocks={stocks} />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && symbols.length === 0 && (
        <Card className="border-dashed bg-card">
          <CardContent className="py-12 text-center space-y-3">
            <GitCompare className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <div>
              <div className="font-display text-base">Nothing to compare yet</div>
              <p className="text-sm text-muted-foreground mt-1">
                Add up to {MAX_SYMBOLS} CSE symbols above, or browse the{" "}
                <Link to="/stocks" className="text-primary hover:underline">Screener</Link>{" "}
                and use "Compare Selected".
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComparePage;
